"use client";

import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatCurrency } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface AddOn {
  id: number;
  name: string;
  add_on_type: string;
  pricing: {
    price: number;
    vat_percentage: number;
  };
}

interface SelectedAddOn {
  add_on_id: number;
  price_type: "fixed" | "per_person";
}

interface BookingAddOnsSelectorProps {
  venueId: number;
  selectedAddOns: SelectedAddOn[];
  onAddOnsChange: (addOns: SelectedAddOn[]) => void;
  participantCount?: number;
}

export function BookingAddOnsSelector({
  venueId,
  selectedAddOns,
  onAddOnsChange,
  participantCount = 1,
}: BookingAddOnsSelectorProps) {
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAddOns();
  }, [venueId]);

  const fetchAddOns = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/add-ons`);
      if (!response.ok) throw new Error("Failed to fetch add-ons");
      const data = await response.json();
      setAddOns(data);
    } catch (error) {
      console.error("Error fetching add-ons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOnToggle = (addOnId: number) => {
    const existing = selectedAddOns.find((sa) => sa.add_on_id === addOnId);

    if (existing) {
      onAddOnsChange(selectedAddOns.filter((sa) => sa.add_on_id !== addOnId));
    } else {
      onAddOnsChange([
        ...selectedAddOns,
        { add_on_id: addOnId, price_type: "fixed" },
      ]);
    }
  };

  const handlePriceTypeChange = (
    addOnId: number,
    priceType: "fixed" | "per_person",
  ) => {
    onAddOnsChange(
      selectedAddOns.map((sa) =>
        sa.add_on_id === addOnId ? { ...sa, price_type: priceType } : sa,
      ),
    );
  };

  const isSelected = (addOnId: number) => {
    return selectedAddOns.some((sa) => sa.add_on_id === addOnId);
  };

  const getPriceType = (addOnId: number) => {
    return (
      selectedAddOns.find((sa) => sa.add_on_id === addOnId)?.price_type ||
      "fixed"
    );
  };

  const calculatePrice = (addOn: AddOn, priceType: "fixed" | "per_person") => {
    const basePrice = addOn.pricing.price;
    const price =
      priceType === "per_person" ? basePrice * participantCount : basePrice;
    const vat = price * (addOn.pricing.vat_percentage / 100);
    return {
      subtotal: price,
      vat: vat,
      total: price + vat,
    };
  };

  const getAddOnTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      food: "Food",
      drink: "Drink",
      service: "Service",
      equipment: "Equipment",
      other: "Other",
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const groupedAddOns = addOns.reduce(
    (acc, addOn) => {
      if (!acc[addOn.add_on_type]) {
        acc[addOn.add_on_type] = [];
      }
      acc[addOn.add_on_type].push(addOn);
      return acc;
    },
    {} as Record<string, AddOn[]>,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add-Ons</CardTitle>
        <CardDescription>
          Select optional add-ons for this booking
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(groupedAddOns).map(([type, typeAddOns]) => (
          <div key={type} className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">
              {getAddOnTypeLabel(type)}
            </h4>
            <div className="space-y-4">
              {typeAddOns.map((addOn) => {
                const selected = isSelected(addOn.id);
                const priceType = getPriceType(addOn.id);
                const pricing = calculatePrice(addOn, priceType);

                return (
                  <div
                    key={addOn.id}
                    className="space-y-3 rounded-lg border p-4"
                  >
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id={`addon-${addOn.id}`}
                        checked={selected}
                        onCheckedChange={() => handleAddOnToggle(addOn.id)}
                      />
                      <div className="flex-1 space-y-1">
                        <Label
                          htmlFor={`addon-${addOn.id}`}
                          className="cursor-pointer"
                        >
                          <div className="font-medium">{addOn.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Base price: {formatCurrency(addOn.pricing.price)}{" "}
                            (VAT: {addOn.pricing.vat_percentage}%)
                          </div>
                        </Label>

                        {selected && (
                          <div className="mt-3 space-y-3">
                            <RadioGroup
                              value={priceType}
                              onValueChange={(value) =>
                                handlePriceTypeChange(
                                  addOn.id,
                                  value as "fixed" | "per_person",
                                )
                              }
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="fixed"
                                  id={`fixed-${addOn.id}`}
                                />
                                <Label
                                  htmlFor={`fixed-${addOn.id}`}
                                  className="cursor-pointer"
                                >
                                  Fixed price
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="per_person"
                                  id={`per-person-${addOn.id}`}
                                />
                                <Label
                                  htmlFor={`per-person-${addOn.id}`}
                                  className="cursor-pointer"
                                >
                                  Per person ({participantCount} participants)
                                </Label>
                              </div>
                            </RadioGroup>

                            <div className="rounded bg-muted p-3 text-sm">
                              <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{formatCurrency(pricing.subtotal)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>VAT:</span>
                                <span>{formatCurrency(pricing.vat)}</span>
                              </div>
                              <div className="flex justify-between font-medium">
                                <span>Total:</span>
                                <span>{formatCurrency(pricing.total)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {addOns.length === 0 && (
          <div className="text-center text-muted-foreground py-4">
            No add-ons available for this venue
          </div>
        )}
      </CardContent>
    </Card>
  );
}
